import type {
  ServiceStockClips,
  StockClipSummary,
} from '@borradh-workspace/api-client/types';
import {
  MAX_CHOSEN_STOCK_CLIPS,
  bodyRegionLabels,
} from '@borradh-workspace/api-client/types';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronsUpDown,
  Plus,
  Search,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { cn } from '@/lib/utils';

import { useSetServiceStockClips, useStockClipsForReview } from '../../api';
import { StockClipPlayer } from './stock-clip-player';
import { type Technique, clipTitle } from './stock-clip-tags-editor';

const BANK_PAGE_SIZE = 12;

/** Which part of the live bank the admin is browsing. */
type BankFilter =
  | { kind: 'technique'; slug: string }
  | { kind: 'generic' }
  | { kind: 'all' };

interface ChooseServiceClipsDialogProps {
  organizationId: string;
  service: ServiceStockClips | null;
  /** The clips the clinic's services use now, by id. */
  clipsById: Map<string, StockClipSummary>;
  techniques: Technique[];
  onOpenChange: (open: boolean) => void;
}

/**
 * Choose the stock clips one service's videos use. The chosen clips replace
 * the automatic match, in the order shown; choosing none goes back to it.
 */
export function ChooseServiceClipsDialog({
  organizationId,
  service,
  clipsById,
  techniques,
  onOpenChange,
}: ChooseServiceClipsDialogProps) {
  return (
    <Dialog open={service !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
        {service ? (
          <ChooseServiceClips
            // A fresh editor per service, so nothing carries over.
            key={service.id}
            organizationId={organizationId}
            service={service}
            clipsById={clipsById}
            techniques={techniques}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ChooseServiceClips({
  organizationId,
  service,
  clipsById,
  techniques,
  onClose,
}: {
  organizationId: string;
  service: ServiceStockClips;
  clipsById: Map<string, StockClipSummary>;
  techniques: Technique[];
  onClose: () => void;
}) {
  const initial = service.mode === 'chosen' ? service.clipIds : [];
  const [excluded, setExcluded] = useState<string[]>(
    service.excludedStockClipIds ?? []
  );
  const [chosen, setChosen] = useState<string[]>(initial);
  // Clips picked from the bank, so the chosen list can show them.
  const [picked, setPicked] = useState<Map<string, StockClipSummary>>(
    () => new Map()
  );
  const { setServiceClips, isSaving } = useSetServiceStockClips(organizationId);

  const clipFor = (id: string) => picked.get(id) ?? clipsById.get(id);

  const changed =
    JSON.stringify(excluded) !==
      JSON.stringify(service.excludedStockClipIds ?? []) ||
    chosen.length !== initial.length ||
    chosen.some((id, i) => id !== initial[i]);
  const isFull = chosen.length >= MAX_CHOSEN_STOCK_CLIPS;

  const add = (clip: StockClipSummary) => {
    if (chosen.includes(clip.id) || isFull) return;
    setPicked((m) => new Map(m).set(clip.id, clip));
    setChosen((ids) => [...ids, clip.id]);
  };
  const remove = (id: string) =>
    setChosen((ids) => ids.filter((x) => x !== id));
  const move = (index: number, by: -1 | 1) =>
    setChosen((ids) => {
      const next = [...ids];
      const [id] = next.splice(index, 1);
      next.splice(index + by, 0, id as string);
      return next;
    });
  const save = (ids: string[]) =>
    setServiceClips(service.id, ids, () => onClose(), excluded);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Choose clips for {service.name}</DialogTitle>
        <DialogDescription>
          Videos for this service use the chosen clips, in this order, instead
          of the automatic match.
        </DialogDescription>
      </DialogHeader>

      <section className="space-y-3">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-medium text-sm">
            Chosen ({chosen.length} of {MAX_CHOSEN_STOCK_CLIPS})
          </h3>
          {chosen.length > 0 ? (
            <button
              type="button"
              className="text-muted-foreground text-xs underline-offset-2 hover:underline"
              onClick={() => setChosen([])}
            >
              Clear
            </button>
          ) : null}
        </div>

        {chosen.length === 0 ? (
          <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed p-4 text-sm sm:flex-row sm:items-center sm:justify-between">
            <p className="text-muted-foreground">
              No clips chosen. This service uses the automatic match.
            </p>
            {service.mode === 'automatic' && service.clipIds.length > 0 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setChosen(service.clipIds)}
              >
                Start from the automatic clips ({service.clipIds.length})
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {chosen.map((id, index) => {
              const clip = clipFor(id);
              return (
                <Card key={id} className="gap-2 py-2">
                  <CardContent className="space-y-2 px-2">
                    {clip ? <StockClipPlayer clip={clip} /> : null}
                    <div className="flex items-center justify-between gap-1">
                      <span className="truncate text-xs">
                        <span className="font-medium">{index + 1}.</span>{' '}
                        {clip ? clipTitle(clip, techniques) : 'Clip'}
                      </span>
                      <div className="flex shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7"
                          aria-label="Use earlier"
                          disabled={index === 0}
                          onClick={() => move(index, -1)}
                        >
                          <ArrowLeft className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7"
                          aria-label="Use later"
                          disabled={index === chosen.length - 1}
                          onClick={() => move(index, 1)}
                        >
                          <ArrowRight className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7"
                          aria-label="Remove"
                          onClick={() => remove(id)}
                        >
                          <X className="size-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <BankBrowser
        service={service}
        techniques={techniques}
        chosen={chosen}
        isFull={isFull}
        onAdd={add}
        onRemove={remove}
        excluded={excluded}
        onExclude={(id) => {
          remove(id);
          setExcluded((ids) => [...new Set([...ids, id])]);
        }}
        onRestore={(id) => setExcluded((ids) => ids.filter((x) => x !== id))}
      />

      {excluded.length ? (
        <section className="space-y-2">
          <h3 className="font-medium text-sm">Excluded from this service</h3>
          <p className="text-sm text-muted-foreground">
            These clips stay blocked, even after an automatic refresh.
          </p>
          {excluded.map((id, index) => (
            <div key={id} className="space-y-2 rounded-md border p-3">
              {clipFor(id) ? (
                <StockClipPlayer clip={clipFor(id) as StockClipSummary} />
              ) : null}
              <span className="truncate text-sm">
                {clipFor(id)?.visualDescription ?? `Excluded clip ${index + 1}`}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setExcluded((ids) => ids.filter((x) => x !== id))
                }
              >
                Restore
              </Button>
            </div>
          ))}
        </section>
      ) : null}
      <DialogFooter className="gap-2 sm:justify-between">
        {service.mode === 'chosen' ? (
          <Button variant="ghost" disabled={isSaving} onClick={() => save([])}>
            Go back to the automatic match
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            disabled={isSaving || !changed || false}
            onClick={() => save(chosen)}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

/** The live stock bank (not rejected), filtered by treatment and searchable. */
function BankBrowser({
  service,
  techniques,
  chosen,
  isFull,
  onAdd,
  onRemove,
  excluded,
  onExclude,
  onRestore,
}: {
  service: ServiceStockClips;
  techniques: Technique[];
  chosen: string[];
  isFull: boolean;
  onAdd: (clip: StockClipSummary) => void;
  onRemove: (id: string) => void;
  excluded: string[];
  onExclude: (id: string) => void;
  onRestore: (id: string) => void;
}) {
  const [filter, setFilter] = useState<BankFilter>(
    service.techniqueSlug
      ? { kind: 'technique', slug: service.techniqueSlug }
      : { kind: 'all' }
  );
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  // A new filter or search starts from the first page.
  const pageKey = `${JSON.stringify(filter)}|${debouncedSearch}`;
  const [paging, setPaging] = useState({ key: pageKey, offset: 0 });
  const offset = paging.key === pageKey ? paging.offset : 0;
  const setOffset = (next: (o: number) => number) =>
    setPaging({ key: pageKey, offset: next(offset) });

  const { page, isLoading, isFetching, isError, error } =
    useStockClipsForReview({
      status: 'live',
      eligibleForServiceId: service.id,
      offset,
      limit: BANK_PAGE_SIZE,
      ...(filter.kind === 'technique' ? { techniqueSlug: filter.slug } : {}),
      ...(filter.kind === 'generic' ? { generic: true } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    });
  const items = page?.items ?? [];

  return (
    <section className="space-y-3">
      <h3 className="font-medium text-sm">
        Eligible clips from the stock bank
      </h3>
      <div className="flex flex-col gap-2 sm:flex-row">
        <BankFilterPicker
          value={filter}
          techniques={techniques}
          serviceTechniqueSlug={service.techniqueSlug}
          onChange={setFilter}
        />
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search what the clip shows"
            aria-label="Search clips"
            className="pl-8"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="aspect-video w-full rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-destructive text-sm">
          {error?.message || 'Could not load the stock bank.'}
        </p>
      ) : items.length === 0 ? (
        <Empty className="border border-dashed">
          <EmptyHeader>
            <EmptyTitle>No clips here</EmptyTitle>
            <EmptyDescription>
              Try another treatment, or clear the search.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((clip) => {
            const isChosen = chosen.includes(clip.id);
            return (
              <Card key={clip.id} className="gap-2 py-2">
                <CardContent className="space-y-2 px-2">
                  <StockClipPlayer clip={clip} />
                  <div className="space-y-1.5">
                    <p className="font-medium text-sm">
                      {clipTitle(clip, techniques)}
                    </p>
                    {clip.regions.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {clip.regions.map((r) => (
                          <Badge key={r} variant="secondary">
                            {bodyRegionLabels[r]}
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                    {clip.visualDescription ? (
                      <p className="line-clamp-2 text-muted-foreground text-xs">
                        {clip.visualDescription}
                      </p>
                    ) : null}
                  </div>
                  <Button
                    size="sm"
                    variant={isChosen ? 'secondary' : 'outline'}
                    className="w-full"
                    disabled={
                      excluded.includes(clip.id) || (!isChosen && isFull)
                    }
                    onClick={() => (isChosen ? onRemove(clip.id) : onAdd(clip))}
                  >
                    {isChosen ? (
                      <>
                        <Check className="size-4" />
                        Chosen
                      </>
                    ) : (
                      <>
                        <Plus className="size-4" />
                        {isFull ? 'Limit reached' : 'Add'}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() =>
                      excluded.includes(clip.id)
                        ? onRestore(clip.id)
                        : onExclude(clip.id)
                    }
                  >
                    {excluded.includes(clip.id)
                      ? 'Restore for this service'
                      : 'Exclude from this service'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {offset > 0 || items.length === BANK_PAGE_SIZE ? (
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || isFetching}
            onClick={() => setOffset((o) => Math.max(0, o - BANK_PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={items.length < BANK_PAGE_SIZE || isFetching}
            onClick={() => setOffset((o) => o + BANK_PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function BankFilterPicker({
  value,
  techniques,
  serviceTechniqueSlug,
  onChange,
}: {
  value: BankFilter;
  techniques: Technique[];
  serviceTechniqueSlug: string | null;
  onChange: (filter: BankFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  const label = useMemo(() => {
    if (value.kind === 'all') return 'All treatments';
    if (value.kind === 'generic') return 'Generic clips';
    return (
      techniques.find((t) => t.slug === value.slug)?.displayName ?? value.slug
    );
  }, [value, techniques]);
  const choose = (filter: BankFilter) => {
    onChange(filter);
    setOpen(false);
  };
  const isSelected = (filter: BankFilter) =>
    filter.kind === value.kind &&
    (filter.kind !== 'technique' ||
      (value.kind === 'technique' && filter.slug === value.slug));
  const serviceTechnique = techniques.find(
    (t) => t.slug === serviceTechniqueSlug
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Treatment"
          className="justify-between font-normal sm:w-64"
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search treatments..." />
          <CommandList>
            <CommandEmpty>No treatment matches.</CommandEmpty>
            <CommandGroup>
              {serviceTechnique ? (
                <FilterItem
                  label={`${serviceTechnique.displayName} (this service)`}
                  value={`this service ${serviceTechnique.displayName}`}
                  selected={isSelected({
                    kind: 'technique',
                    slug: serviceTechnique.slug,
                  })}
                  onSelect={() =>
                    choose({ kind: 'technique', slug: serviceTechnique.slug })
                  }
                />
              ) : null}
              <FilterItem
                label="Generic clips"
                value="generic no treatment"
                selected={isSelected({ kind: 'generic' })}
                onSelect={() => choose({ kind: 'generic' })}
              />
              <FilterItem
                label="All treatments"
                value="all treatments everything"
                selected={isSelected({ kind: 'all' })}
                onSelect={() => choose({ kind: 'all' })}
              />
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Treatments">
              {techniques.map((t) => (
                <FilterItem
                  key={t.slug}
                  label={t.displayName}
                  value={`${t.displayName} ${t.slug}`}
                  selected={isSelected({ kind: 'technique', slug: t.slug })}
                  onSelect={() => choose({ kind: 'technique', slug: t.slug })}
                />
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FilterItem({
  label,
  value,
  selected,
  onSelect,
}: {
  label: string;
  value: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <CommandItem value={value} onSelect={onSelect}>
      <Check
        className={cn('mr-2 size-4', selected ? 'opacity-100' : 'opacity-0')}
      />
      {label}
    </CommandItem>
  );
}
